import React from 'react';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Tag, theme, Space } from 'antd';
import { Field } from '../../../components/Field';

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

const sourceColors: Record<string, string> = {
  OPENROUTER: '#1890ff',
  MANUAL: '#52c41a',
};

export const LlmModelShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow({ resource: 'llm-models' });
  const item = queryResult?.data?.data as any;

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  const formatPrice = (v: string | null | undefined) => {
    if (!v) return '—';
    const num = parseFloat(v);
    if (num === 0) return 'Free';
    const perM = num * 1_000_000;
    if (perM < 0.01) return `<$0.01/1M tok`;
    return `$${perM.toFixed(2).replace(/\.?0+$/, '')}/1M tok`;
  };

  return (
    <Show resource="llm-models">
      <div style={{ padding: '8px 0' }}>
        <Field label="Slug">{item.slug}</Field>
        <Field label="Name">{item.name}</Field>
        <Field label="Source">
          <Tag style={tagStyle(sourceColors[item.source] || '#898989')}>{item.source}</Tag>
        </Field>
        {item.description && <Field label="Description"><div style={{ maxHeight: 200, overflow: 'auto' }}>{item.description}</div></Field>}

        <h4 style={{ marginTop: 24, marginBottom: 8 }}>Context</h4>
        <Field label="Context Length">{item.contextLength?.toLocaleString() || '—'}</Field>
        <Field label="Max Completion Tokens">{item.maxCompletionTokens?.toLocaleString() || '—'}</Field>

        <h4 style={{ marginTop: 24, marginBottom: 8 }}>Architecture</h4>
        <Field label="Modality">{item.modality || '—'}</Field>
        <Field label="Input Modalities">
          {item.inputModalities?.length ? (
            <Space wrap size={4}>{item.inputModalities.map((m: string) => <Tag key={m}>{m}</Tag>)}</Space>
          ) : '—'}
        </Field>
        <Field label="Output Modalities">
          {item.outputModalities?.length ? (
            <Space wrap size={4}>{item.outputModalities.map((m: string) => <Tag key={m}>{m}</Tag>)}</Space>
          ) : '—'}
        </Field>
        <Field label="Tokenizer">{item.tokenizer || '—'}</Field>
        <Field label="Instruct Type">{item.instructType || '—'}</Field>

        <h4 style={{ marginTop: 24, marginBottom: 8 }}>Pricing (per 1M tokens)</h4>
        <Field label="Prompt">{formatPrice(item.promptPrice)}</Field>
        <Field label="Completion">{formatPrice(item.completionPrice)}</Field>
        <Field label="Input Cache Read">{formatPrice(item.inputCacheReadPrice)}</Field>
        <Field label="Image">{formatPrice(item.imagePrice)}</Field>
        <Field label="Web Search">{formatPrice(item.webSearchPrice)}</Field>
        <Field label="Internal Reasoning">{formatPrice(item.internalReasoningPrice)}</Field>

        <h4 style={{ marginTop: 24, marginBottom: 8 }}>Other</h4>
        <Field label="Supported Parameters">
          {item.supportedParameters?.length ? (
            <Space wrap size={4}>{item.supportedParameters.map((p: string) => <Tag key={p} style={{ fontSize: 11 }}>{p}</Tag>)}</Space>
          ) : '—'}
        </Field>
        <Field label="Active">
          <Tag style={tagStyle(item.isActive ? '#3ecf8e' : '#898989')}>{item.isActive ? 'Active' : 'Inactive'}</Tag>
        </Field>
        <Field label="Expiration Date">{item.expirationDate ? new Date(item.expirationDate).toLocaleDateString() : '—'}</Field>
        <Field label="Provider">{item.providerName || item.providerId || '—'}</Field>
        {item.createdAt && <Field label="Created At">{new Date(item.createdAt).toLocaleString()}</Field>}
        {item.updatedAt && <Field label="Updated At">{new Date(item.updatedAt).toLocaleString()}</Field>}
      </div>
    </Show>
  );
};
